import { createContext, useContext } from 'react'
import { GitHubIcon } from '@/components/GitHubIcon'
import { useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DEFAULT_AUTO_START_RULE, GITHUB_TOKEN_SECRET, ProjectSettings } from '@one-deployment/api'
import type { ProjectFileExport, ProjectFileReread, SecretBulkResult, ProjectFileStatus, BranchList, FeedbackList, Integrations, LinearOptions, Project, ProjectPatch, SecretList } from '@one-deployment/api'
import { Button as SButton } from '@/components/shadcn/button'
import { Button, Card as UiCard, Input, Label, Listbox, PageHeader, Select, SkeletonRows, Switch, Table, Td, Textarea, Tr, type ListboxOption } from '@/components/ui'
import { Age, EmptyState, ErrorState, Mono } from '@/components/status'
import { RoleGate, useCan } from '@/components/RoleGate'
import { ApiError, del, enc, get, patch, post, put } from '@/lib/api'
import { cn } from '@/lib/cn'
import { dateTime } from '@/lib/format'
import { qk } from '@/lib/query'
import { useProject } from './data'
import { RepoMissingBanner } from './RepoMissing'
import { ask } from '@/components/ask'
import { SecretsTab } from './SecretsTab'
import { McpSettings } from './Mcp'

/** In the General section cards drop their frame and heading; actions stay top right. */
const Bare = createContext(false)
function Card(props: React.ComponentProps<typeof UiCard>) {
  const bare = useContext(Bare)
  if (!bare) return <UiCard {...props} />
  return (
    <div className="relative">
      {props.actions && <div className="absolute top-3 right-3 flex items-center gap-1">{props.actions}</div>}
      {props.children}
    </div>
  )
}

const SECTIONS = [
  ['general', 'General'],
  ['linear', 'Linear'],
  ['secrets', 'Secrets'],
  ['claude', 'Claude Code integrations'],
  ['other', 'Other'],
] as const
export type SettingsSection = (typeof SECTIONS)[number][0]

/** Older links name the section a card used to have; send them to its group. */
function sectionFor(s: string): SettingsSection {
  if (s === 'linear' || s === 'tickets') return 'linear'
  if (s === 'secrets') return 'secrets'
  if (s === 'claude' || s === 'mcp') return 'claude'
  if (s === 'other' || s === 'containers' || s === 'scripts') return 'other'
  return 'general'
}

export function useIntegrations(enabled = true) {
  return useQuery({ queryKey: qk.integrations(), queryFn: ({ signal }) => get<Integrations>('/api/integrations', signal), enabled })
}

/** Project settings (03 §5.11–5.13). Lead and above; secret values are viewable there, each view audited. */
export function ProjectSettingsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { project, section: raw } = useParams({ strict: false }) as { project: string; section: string }
  const section = sectionFor(raw)
  const allowed = useCan('project.settings', project)
  const proj = useProject(project)

  if (!allowed) return <EmptyState title="Settings are for leads" body="Ask a lead or an admin on this project to change its settings." />

  const p = proj.data
  return (
    <div className="flex h-full flex-col">
      {/* Inside the project page's Settings tab the page already has a header. */}
      {!embedded && p && <RepoMissingBanner project={p} />}
      {!embedded && <PageHeader title="Settings" meta={p?.name ?? project} />}
      <div className="flex min-h-0 flex-1 flex-col">
        <nav role="tablist" className="flex shrink-0 gap-1 px-4 pt-3">
          {SECTIONS.map(([key, label]) => (
            <Link key={key} role="tab" aria-selected={section === key} to="/p/$project/settings/$section" params={{ project, section: key }}
              className={cn('flex h-7 items-center rounded-sm px-2.5 text-sm', section === key ? 'bg-surface-3 text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg')}>{label}</Link>
          ))}
        </nav>
        <div className="min-h-0 min-w-0 flex-1 overflow-auto p-4">
          {proj.error && !p ? <ErrorState error={proj.error} retry={() => void proj.refetch()} /> : !p ? <SkeletonRows rows={6} /> : (
            // Keyed on the version: after a save or somebody else's edit, every form starts from what is true now.
            <div key={p.version}>
              {section === 'general' && (
                <Bare.Provider value>
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <div><Repo p={p} /><General p={p} /></div>
                    <div><ProjectFileCard p={p} /><GitSafety p={p} /><CiCard p={p} /></div>
                  </div>
                </Bare.Provider>
              )}
              {section === 'linear' && <LinearTab p={p} />}
              {section === 'secrets' && <SecretsTab project={project} />}
              {section === 'claude' && <McpSettings project={project} />}
              {section === 'other' && <OtherTab p={p} project={project} />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Every save names the version it was editing (`If-Match`). If somebody else
 * saved first the Worker answers 409, the query client refetches, and the form
 * re-mounts on the new version for the person to look again.
 */
export function useSave(p: Project) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ProjectPatch) => patch<Project>(`/api/projects/${enc(p.key)}`, body, p.version),
    onSuccess: (next) => { qc.setQueryData(qk.project(p.key), next); void qc.invalidateQueries({ queryKey: qk.projects() }); void qc.invalidateQueries({ queryKey: qk.overview() }) },
  })
}

function SaveRow({ m, dirty }: { m: ReturnType<typeof useSave>; dirty: boolean }) {
  return (
    <div className="flex items-center gap-3 px-3 pb-3">
      <Button type="submit" variant="primary" disabled={!dirty || m.isPending}>Save</Button>
      {m.isSuccess && !dirty && <span className="text-xs text-ok">Saved.</span>}
      {m.error && <ErrorState error={m.error} compact />}
    </div>
  )
}

function General({ p }: { p: Project }) {
  const [name, setName] = useState(p.name)
  const m = useSave(p)
  return (
    <Card title="General">
      <form onSubmit={(e) => { e.preventDefault(); m.mutate({ name }) }}>
        <div className="space-y-3 p-3">
          <div><Label htmlFor="g-name">Name</Label><Input id="g-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
        </div>
        <SaveRow m={m} dirty={name !== p.name} />
      </form>
    </Card>
  )
}

export function Repo({ p }: { p: Project }) {
  const qc = useQueryClient()
  const can = useCan('project.repo.reconnect', p.key)
  const [picking, setPicking] = useState(p.repo.state !== 'ok')
  const [repoId, setRepoId] = useState<number | null>(null)
  const ints = useIntegrations(picking && can)
  const connect = useMutation({
    mutationFn: () => post<Project>(`/api/projects/${enc(p.key)}/repo`, { repoId }),
    onSuccess: (next) => { setPicking(false); qc.setQueryData(qk.project(p.key), next) },
  })
  const r = p.repo
  const repos = (ints.data?.github.repos ?? []).filter((x) => !x.linkedProject || x.linkedProject === p.key)
  return (
    <Card title="Repository" actions={
      <div className="flex items-center gap-2">
        {can && !picking && p.githubAuth === 'app' && <Button onClick={() => setPicking(true)}>Change</Button>}
        <span className={cn('size-2 rounded-full', r.state === 'ok' ? 'bg-ok' : 'bg-err')}
          title={r.state === 'ok' ? 'Reachable' : `Missing${r.missingReason ? ` — ${r.missingReason}` : ''}`} />
      </div>
    }>
      <div className="p-3 text-sm">
        <div className="flex items-center gap-2"><GitHubIcon size={16} className="text-fg-muted" /><span className="text-fg-muted">{r.owner}</span><span className="text-fg-faint">/</span><span>{r.name}</span></div>
        {picking && can && p.githubAuth === 'app' && (
          <div className="mt-4">
            {ints.error ? <ErrorState error={ints.error} compact /> : !ints.data ? <SkeletonRows rows={3} /> : repos.length === 0 ? (
              <p className="text-fg-muted">The GitHub App can see no free repo. <InstallLink kind="github">Install it or grant it a repo</InstallLink>, then reload this section.</p>
            ) : (
              <>
                <Label htmlFor="repo-pick">Pick the repo (a recreated repo has a new id — pick it here)</Label>
                <Select id="repo-pick" className="w-full" value={repoId ?? ''} onChange={(e) => setRepoId(Number(e.target.value) || null)}>
                  <option value="">Choose…</option>
                  {repos.map((x) => <option key={x.id} value={x.id}>{x.owner}/{x.name}</option>)}
                </Select>
                <div className="mt-2 flex gap-2">
                  <Button variant="primary" disabled={!repoId || connect.isPending} onClick={() => connect.mutate()}>Connect</Button>
                  {r.state === 'ok' && <Button onClick={() => setPicking(false)}>Cancel</Button>}
                </div>
              </>
            )}
            {connect.error && <ErrorState error={connect.error} compact />}
          </div>
        )}
      </div>
    </Card>
  )
}

/** The install URLs are minted by the Worker (they carry a signed state), so ask for one and go. */
export function InstallLink({ kind, children }: { kind: 'github' | 'linear'; children: React.ReactNode }) {
  return (
    <SButton type="button" variant="link" className="h-auto p-0 text-[length:inherit] font-normal text-accent-hover" onClick={() => void get<{ url: string }>(`/api/integrations/${kind}/install`).then((r) => window.open(r.url, '_blank', 'noopener'))}>
      {children}
    </SButton>
  )
}

type FlowKey = 'linearTriggerState' | 'linearBuildState' | 'linearPrState' | 'linearMergedState'

/** A team's mark: its key on Linear's team color, or a steady color picked from the key. */
function TeamAvatar({ teamKey, color, size = 18 }: { teamKey: string; color?: string | null; size?: number }) {
  const hue = [...teamKey].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) % 360, 7)
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-[5px] font-semibold text-white" aria-hidden
      style={{ width: size, height: size, fontSize: size * 0.42, background: color ?? `hsl(${hue} 55% 45%)` }}>{teamKey.slice(0, 3)}</span>
  )
}

const TYPE_COLOR: Record<string, string> = { backlog: '#95a2b3', unstarted: '#e2e2e2', started: '#f2c94c', completed: '#5e6ad2', canceled: '#95a2b3', triage: '#fc7840' }
function Dot({ color, ring }: { color?: string | null; ring?: boolean }) {
  return <span aria-hidden className={cn('inline-block size-2.5 shrink-0 rounded-full', ring && 'border border-dashed border-fg-faint')} style={ring ? undefined : { background: color ?? '#95a2b3' }} />
}

/** Our stages, each with its own fixed dot: white, yellow, red, green. */
const FLOW: { key: FlowKey; title: string; hint: string; none: string; dot: string }[] = [
  { key: 'linearTriggerState', title: 'Incoming', hint: 'Ready to start. Moving a ticket here starts a build.', none: 'None — only delegation starts work', dot: '#e5e5e5' },
  { key: 'linearBuildState', title: 'While building', hint: 'When the agent starts work.', none: "Default — the team's first started state", dot: '#f2c94c' },
  { key: 'linearPrState', title: 'PR opened', hint: 'When the pull request opens.', none: "Don't move", dot: '#eb5757' },
  { key: 'linearMergedState', title: 'PR merged', hint: 'When the pull request is merged.', none: "Don't move", dot: '#4cb782' },
]

/**
 * The Linear tab: team, project and branch on one row, the ticket switches
 * under them, then our stages mapped to the team's states. One save for all.
 */
function LinearTab({ p }: { p: Project }) {
  const st = p.settings
  const init = {
    team: p.linearTeamId ?? '',
    project: p.linearProjectId ?? '',
    branch: p.targetBranch ?? '',
    parallelCap: String(st.parallelCap ?? 4),
    resumeAfterMin: String(st.resumeAfterMin ?? 10),
    chatCap: String(st.chatCap ?? 2),
    areas: (st.areas ?? []).join(', '),
    linearTriggerState: st.linearTriggerState ?? '',
    linearBuildState: st.linearBuildState ?? '',
    linearPrState: st.linearPrState ?? st.linearReviewState ?? '',
    linearMergedState: st.linearMergedState ?? '',
    autoStart: st.autoStart?.on ?? false,
    autoStartRule: st.autoStart?.rule || DEFAULT_AUTO_START_RULE,
    leadLinearUserId: st.leadLinearUserId ?? '',
    agentLinearUserId: st.agentLinearUserId ?? '',
    maxOpenTickets: String(st.maxOpenTickets ?? 5),
  }
  const [f, setF] = useState(init)
  const set = <K extends keyof typeof init>(k: K, v: (typeof init)[K]) => setF((o) => ({ ...o, [k]: v }))
  const m = useSave(p)
  const ints = useIntegrations()
  const orgs = ints.data?.linear.orgs ?? []
  const opts = useQuery({
    queryKey: ['project', p.key, 'linear-options', f.team],
    queryFn: ({ signal }) => get<LinearOptions>(`/api/projects/${enc(p.key)}/linear-options?team=${enc(f.team)}`, signal),
    enabled: !!f.team,
    staleTime: 60_000,
  })
  const o = f.team ? opts.data : undefined
  const branches = useQuery({
    queryKey: ['project', p.key, 'branches'],
    queryFn: ({ signal }) => get<BranchList>(`/api/projects/${enc(p.key)}/branches`, signal),
    staleTime: 60_000,
  })
  // Empty means the repo's default; a saved branch GitHub no longer lists stays visible.
  const branchOptions: ListboxOption<string>[] = [
    { value: '', label: `Default (${p.repo.defaultBranch})` },
    ...(branches.data?.branches ?? [p.repo.defaultBranch, ...(f.branch && f.branch !== p.repo.defaultBranch ? [f.branch] : [])])
      .map((b, i) => ({ value: b, label: b, sep: i === 0 })),
  ]

  const cap = Number(f.parallelCap)
  const quiet = Number(f.resumeAfterMin)
  const chats = Number(f.chatCap)
  const maxOpen = Number(f.maxOpenTickets)
  const valid = Number.isInteger(cap) && cap >= 1 && cap <= 32 && Number.isInteger(quiet) && quiet >= 1 && quiet <= 240
    && Number.isInteger(chats) && chats >= 1 && chats <= 16 && Number.isInteger(maxOpen) && maxOpen >= 1 && maxOpen <= 100
  const dirty = JSON.stringify(f) !== JSON.stringify(init)
  const save = () => {
    if (!valid) return
    const settings: NonNullable<ProjectPatch['settings']> = {
      parallelCap: cap, resumeAfterMin: quiet, chatCap: chats, maxOpenTickets: maxOpen,
      areas: f.areas.split(',').map((a) => a.trim()).filter(Boolean),
      autoStart: { on: f.autoStart, rule: f.autoStartRule.trim() || DEFAULT_AUTO_START_RULE },
    }
    const clear: NonNullable<ProjectPatch['clearSettings']> = ['linearReviewState']
    for (const { key } of FLOW) { if (f[key]) settings[key] = f[key]; else clear.push(key) }
    for (const key of ['leadLinearUserId', 'agentLinearUserId'] as const) { if (f[key]) settings[key] = f[key]; else clear.push(key) }
    m.mutate({ linearTeamId: f.team || null, linearProjectId: f.project.trim() || null, targetBranch: f.branch.trim() || null, settings, clearSettings: clear })
  }

  const teams = orgs.flatMap((org) => org.teams.map((t) => ({ ...t, org: org.name })))
  const teamOptions: ListboxOption<string>[] = [
    { value: '', label: 'None' },
    ...teams.map((t, i) => ({
      value: t.id, sep: i === 0,
      label: orgs.length > 1 ? `${t.org} · ${t.name}` : t.name,
      icon: <TeamAvatar teamKey={t.key} color={o?.team?.id === t.id ? o.team.color : null} />,
      hint: t.key,
    })),
  ]
  // A saved project the list no longer shows (finished, or Linear unreadable) stays visible by id.
  const projects = o?.projects ?? []
  const projectOptions: ListboxOption<string>[] = [
    { value: '', label: 'All projects in this team', icon: <Dot ring /> },
    ...(f.project && !projects.some((x) => x.id === f.project) ? [{ value: f.project, label: opts.isLoading ? 'Loading…' : `${f.project.slice(0, 8)}… (not listed)`, icon: <Dot />, sep: true }] : []),
    ...projects.map((x, i) => ({ value: x.id, label: x.name, icon: <ProjectIcon color={x.color} />, sep: i === 0 && !(f.project && !projects.some((y) => y.id === f.project)) })),
  ]
  const users = o?.users ?? []
  const userOptions = (value: string): ListboxOption<string>[] => [
    { value: '', label: 'Nobody', icon: <Dot ring /> },
    ...(value && !users.some((u) => u.id === value) ? [{ value, label: opts.isLoading ? 'Loading…' : `${value.slice(0, 8)}… (not listed)`, icon: <Dot />, sep: true }] : []),
    ...users.map((u, i) => ({ value: u.id, label: u.name, hint: u.email ?? undefined, sep: i === 0 })),
  ]
  const states = o?.states ?? []
  const stateOptions = (none: string, value: string): ListboxOption<string>[] => {
    const missing = value && !states.some((s) => s.name.toLowerCase() === value.toLowerCase())
    return [
      { value: '', label: none, icon: <Dot ring /> },
      ...(missing ? [{ value, label: `${value} (not in this team)`, icon: <Dot />, sep: true }] : []),
      ...states.map((s, i) => ({ value: s.name, label: s.name, icon: <Dot color={s.color ?? TYPE_COLOR[s.type]} />, sep: i === 0 && !missing })),
    ]
  }

  return (
    <form className="pb-16" onSubmit={(e) => { e.preventDefault(); save() }}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_1fr_7rem_9rem_9rem]">
        <div className="min-w-0"><Label htmlFor="l-team">Team</Label>
          <Listbox id="l-team" className="w-full" value={f.team} options={teamOptions} placeholder="None"
            onChange={(v) => setF((x) => ({ ...x, team: v, project: v === x.team ? x.project : '' }))} /></div>
        <div className="min-w-0"><Label htmlFor="l-project">Project</Label>
          <Listbox id="l-project" className="w-full" value={f.project} options={projectOptions} disabled={!f.team}
            onChange={(v) => set('project', v)} /></div>
        <div className="min-w-0"><Label htmlFor="l-branch">Build from branch</Label>
          <Listbox id="l-branch" className="w-full font-mono" value={f.branch} options={branchOptions} search onChange={(v) => set('branch', v)} /></div>
        <div><Label htmlFor="t-cap">Tickets at once</Label><Input id="t-cap" className="h-8 w-full" inputMode="numeric" value={f.parallelCap} onChange={(e) => set('parallelCap', e.target.value)} /></div>
        <div><Label htmlFor="t-quiet">Carry on after (min)</Label><Input id="t-quiet" className="h-8 w-full" inputMode="numeric" value={f.resumeAfterMin} onChange={(e) => set('resumeAfterMin', e.target.value)} /></div>
        <div><Label htmlFor="t-chats">Slack chats per box</Label><Input id="t-chats" className="h-8 w-full" inputMode="numeric" value={f.chatCap} onChange={(e) => set('chatCap', e.target.value)} /></div>
      </div>

      <div className="mt-2 space-y-1 text-xs">
        {ints.data && orgs.length === 0 && <p className="text-fg-subtle">No Linear workspace is connected. An admin connects it in <Link to="/settings/connections" className="text-accent hover:underline">Connections</Link>.</p>}
        {orgs.filter((x) => x.guardTripped).map((x) => (
          <p key={x.id} className="text-warn">Linear writes to {x.name} are stopped by the write guard{x.guardReason && `: ${x.guardReason}`}. Resume them in <Link to="/settings/connections" className="text-accent hover:underline">Connections</Link>.</p>
        ))}
        {o?.note && <p className="text-warn">{o.note}.</p>}
        {branches.data?.note && <p className="text-warn">{branches.data.note}.</p>}
        {opts.error && <ErrorState error={opts.error} compact />}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_9rem]">
        <div className="min-w-0"><Label htmlFor="l-lead">Lead</Label>
          <Listbox id="l-lead" className="w-full" value={f.leadLinearUserId} options={userOptions(f.leadLinearUserId)} search disabled={!f.team} onChange={(v) => set('leadLinearUserId', v)} /></div>
        <div className="min-w-0"><Label htmlFor="l-agent">Agent account</Label>
          <Listbox id="l-agent" className="w-full" value={f.agentLinearUserId} options={userOptions(f.agentLinearUserId)} search disabled={!f.team} onChange={(v) => set('agentLinearUserId', v)} /></div>
        <div><Label htmlFor="t-maxopen">Max open per person</Label><Input id="t-maxopen" className="h-8 w-full" inputMode="numeric" value={f.maxOpenTickets} onChange={(e) => set('maxOpenTickets', e.target.value)} /></div>
      </div>
      <p className="mt-1 text-xs text-fg-subtle">After triage: easy and medium tickets go to the agent account, hard ones to the team member with the fewest open tickets (else the lead), very hard ones and ones triage could not judge to the lead.</p>

      {!valid && <p role="alert" className="mt-2 text-xs text-err">Tickets at once is 1 to 32; minutes quiet is 1 to 240; Slack chats per box is 1 to 16; max open per person is 1 to 100.</p>}

      <div className="mt-8">
        {!f.team && <p className="mb-3 text-xs text-fg-subtle">Pick a team to map its states.</p>}
        <ol>
          {FLOW.map((s, i) => {
            return (
              <li key={s.key} className="relative flex gap-4 pb-6 last:pb-0">
                {i < FLOW.length - 1 && <span aria-hidden className="absolute top-6 bottom-0 left-[11px] w-px bg-border" />}
                <span className="relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-surface-2">
                  <span aria-hidden className="size-2.5 rounded-full" style={{ background: s.dot }} />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <label htmlFor={`f-${s.key}`} className="text-sm text-fg">{s.title}</label>
                    <p className="text-xs text-fg-faint">{s.hint}</p>
                  </div>
                  <Listbox id={`f-${s.key}`} className="w-full sm:w-72" value={f[s.key]} options={stateOptions(s.none, f[s.key])} disabled={!f.team} onChange={(v) => set(s.key, v)} />
                </div>
              </li>
            )
          })}
        </ol>
      </div>

      <div className="mt-8 space-y-2">
        <div className="flex items-center justify-between gap-4">
          <label id="l-auto" htmlFor="l-auto-switch" className="text-sm text-fg">Start by itself</label>
          <Switch id="l-auto-switch" aria-labelledby="l-auto" checked={f.autoStart} onChange={(v) => set('autoStart', v)} />
        </div>
        {f.autoStart && (
          <Textarea id="l-auto-rule" aria-label="When to start by itself" rows={2} maxLength={1000} value={f.autoStartRule} onChange={(e) => set('autoStartRule', e.target.value)}
            className="min-h-0 w-full" />
        )}
      </div>

      {(dirty || m.error) && (
        <div className="sticky bottom-0 z-20 -mx-4 mt-8 flex items-center gap-3 border-t border-border bg-surface-1/95 px-4 py-2.5 backdrop-blur">
          <span className="text-sm text-fg-muted">Unsaved changes</span>
          {m.error && <ErrorState error={m.error} compact />}
          <div className="ml-auto flex gap-2">
            <Button type="button" onClick={() => { setF(init); m.reset() }} disabled={m.isPending}>Discard</Button>
            <Button type="submit" variant="primary" disabled={!dirty || !valid || m.isPending}>{m.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      )}
    </form>
  )
}

function ProjectIcon({ color }: { color?: string | null }) {
  return <span aria-hidden className="inline-block size-3 shrink-0 rotate-45 rounded-[3px]" style={{ background: color ?? '#95a2b3' }} />
}

/** The Other tab: containers, scripts, models and test env share one save bar. */
function OtherTab({ p, project }: { p: Project; project: string }) {
  const f = useSettingsForm(p, ['containerMemMb', 'memDefaultMb', 'maxContainers', 'sessionsPerContainer', 'image', 'devCmd', 'devPort', 'testCmd', 'unitCmd', 'e2eCmd', 'uatUrl', 'prodUrl', 'triageModel', 'implementModel'])
  const [env, setEnv] = useState(() => envText(p.settings.testEnv))
  const [envError, setEnvError] = useState<string | null>(null)
  const envDirty = env !== envText(p.settings.testEnv)
  const dirty = f.dirty || envDirty
  const save = () => {
    const parsed = parseEnv(env)
    if (!ProjectSettings.safeParse({ testEnv: parsed }).success) return setEnvError('One KEY=value per line; names in capitals, digits and _.')
    setEnvError(null)
    f.submit({ testEnv: Object.keys(parsed).length ? parsed : undefined })
  }
  return (
    <form className="space-y-6 pb-16" onSubmit={(e) => { e.preventDefault(); save() }}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {f.field('uatUrl', 'UAT URL', 'Verify runs here; feedback is taken from here.')}
        {f.field('prodUrl', 'Production URL')}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {f.field('containerMemMb', 'Memory per container (MB)')}
        {f.field('memDefaultMb', 'Starting memory per step (MB)', '512–65536. Empty = 4096.')}
        {f.field('maxContainers', 'Max containers')}
        {f.field('sessionsPerContainer', 'Sessions per container')}
      </div>
      {f.field('image', 'Image', 'Empty = the platform default.')}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_8rem]">
        {f.field('devCmd', 'Dev server', 'Proves a PR in a browser. Empty = PRs open unproved.')}
        {f.field('devPort', 'Dev port')}
      </div>
      {f.field('testCmd', 'Test', 'Compared against the target branch.')}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {f.field('unitCmd', 'Unit checks', 'Build, lint, typecheck and unit tests; every build. Empty = Test.')}
        {f.field('e2eCmd', 'End-to-end', 'Related spec paths are appended. Flow and dependency only.')}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {f.field('triageModel', 'Triage model', 'Classifies every ticket. Empty = Opus 5.5.')}
        {f.field('implementModel', 'Implementation model', 'Does the work. Empty = Opus 5.5.')}
      </div>
      <div>
        <Label htmlFor="s-testEnv">Test environment</Label>
        <Textarea id="s-testEnv" rows={3} className="min-h-0 w-full font-mono text-xs md:text-xs" placeholder="VITEST_MAX_WORKERS=3"
          value={env} onChange={(e) => setEnv(e.target.value)} />
        {envError ? <p role="alert" className="mt-1 text-2xs text-err">{envError}</p> : <p className="mt-1 text-2xs text-fg-faint">Set for every test run.</p>}
      </div>
        {(dirty || f.m.error) && (
          <div className="sticky bottom-0 z-20 -mx-4 flex items-center gap-3 border-t border-border bg-surface-1/95 px-4 py-2.5 backdrop-blur">
            <span className="text-sm text-fg-muted">Unsaved changes</span>
            {f.m.error && <ErrorState error={f.m.error} compact />}
            <div className="ml-auto flex gap-2">
              <Button type="button" onClick={() => { f.reset(); setEnv(envText(p.settings.testEnv)); setEnvError(null) }} disabled={f.m.isPending}>Discard</Button>
              <Button type="submit" variant="primary" disabled={!dirty || f.m.isPending}>{f.m.isPending ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        )}
    </form>
  )
}

type NumKey = 'containerMemMb' | 'memDefaultMb' | 'maxContainers' | 'sessionsPerContainer' | 'devPort'
type StrKey = 'image' | 'devCmd' | 'testCmd' | 'unitCmd' | 'e2eCmd' | 'uatUrl' | 'prodUrl' | 'triageModel' | 'implementModel' | 'branchPrefix' | 'authorName' | 'authorEmail' | 'prodSupabaseRef' | 'supabaseUrl' | 'slackChannel'

/** A settings form over some of `ProjectSettings`; only the keys it shows are sent. */
function useSettingsForm(p: Project, keys: readonly (NumKey | StrKey)[]) {
  const pick = () => Object.fromEntries(keys.map((k) => [k, p.settings[k] ?? ''])) as Record<string, string | number>
  const [v, setV] = useState(pick)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const m = useSave(p)
  const dirty = JSON.stringify(v) !== JSON.stringify(pick())
  const submit = (extra: ProjectSettings = {}) => {
    const settings: ProjectSettings = { ...extra }
    for (const k of keys) {
      const x = v[k]
      // Empty means "use the default": sent as absent, not as an empty string.
      ;(settings as Record<string, unknown>)[k] = x === '' ? undefined : typeof p.settings[k] === 'number' || NUMS.has(k) ? Number(x) : x
    }
    // Checked here with the same schema the Worker applies, so a bad value is
    // explained beside its field rather than as a 400 toast.
    const r = ProjectSettings.safeParse(settings)
    if (!r.success) {
      setErrors(Object.fromEntries(r.error.issues.map((i) => [String(i.path[0]), i.message])))
      return
    }
    setErrors({})
    m.mutate({ settings: r.data })
  }
  const field = (k: NumKey | StrKey, label: string, hint?: string) => (
    <div>
      <Label htmlFor={`s-${k}`}>{label}</Label>
      <Input id={`s-${k}`} type={NUMS.has(k) ? 'number' : 'text'} min={NUMS.has(k) ? 1 : undefined} className={cn('h-8 w-full', !NUMS.has(k) && 'font-mono text-xs')}
        value={v[k] ?? ''} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
      {errors[k] ? <p role="alert" className="mt-1 text-2xs text-err">{errors[k]}</p> : hint && <p className="mt-1 text-2xs text-fg-faint">{hint}</p>}
    </div>
  )
  const reset = () => { setV(pick()); setErrors({}); m.reset() }
  return { m, dirty, submit, field, reset }
}
const NUMS = new Set<string>(['containerMemMb', 'memDefaultMb', 'maxContainers', 'sessionsPerContainer', 'devPort'])

/** `KEY=value` per line, both ways. */
const envText = (e: Record<string, string> | undefined) => Object.entries(e ?? {}).map(([k, v]) => `${k}=${v}`).join('\n')
function parseEnv(text: string): Record<string, string> {
  return Object.fromEntries(text.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
    const i = l.indexOf('=')
    return i < 0 ? [l, ''] : [l.slice(0, i).trim(), l.slice(i + 1).trim()]
  }))
}

function GitSafety({ p }: { p: Project }) {
  const f = useSettingsForm(p, ['branchPrefix', 'authorName', 'authorEmail', 'prodSupabaseRef', 'supabaseUrl', 'slackChannel'])
  return (
    <Card title="Git & safety">
      <form onSubmit={(e) => { e.preventDefault(); f.submit() }}>
        <div className="space-y-3 p-3">
          {f.field('branchPrefix', 'Branch prefix', 'Ticket branches are <prefix><ticket>-<slug>. Empty = trisof/.')}
          {f.field('authorName', 'Commit author name', 'Author and committer of every commit on this project, the agent\'s own included. Set both or neither.')}
          {f.field('authorEmail', 'Commit author email')}
          {f.field('prodSupabaseRef', 'Production Supabase ref', 'Verify and browser-proof runs refuse to start if any project setting or secret points at this Supabase project: a test run must never write into the client\'s production data.')}
          {f.field('supabaseUrl', 'Production Supabase url', 'For error review, which reads the logs table with the SUPABASE_SERVICE_KEY secret. Empty = https://<ref>.supabase.co.')}
          {f.field('slackChannel', 'Slack channel id', 'For example C0123ABCD. The morning summary and spike alerts go here; invite the trisof-agent bot to the channel.')}
        </div>
        <SaveRow m={f.m} dirty={f.dirty} />
      </form>
    </Card>
  )
}

/** CI from .trisof/ci.yml (features/21). Saves on toggle; `parallel` is kept as it was. */
/** .trisof/project.yml (features/23): the commit in use, a broken one's problems, and what the nightly check found. */
function ProjectFileCard({ p }: { p: Project }) {
  const q = useQuery({ queryKey: ['project-file', p.key], queryFn: ({ signal }) => get<ProjectFileStatus>(`/api/projects/${enc(p.key)}/project-file`, signal) })
  const s = q.data
  const short = (sha: string | null) => <Mono>{sha ? sha.slice(0, 7) : '—'}</Mono>
  const exp = useMutation({ mutationFn: () => get<ProjectFileExport>(`/api/projects/${enc(p.key)}/project-file/export`) })
  const qc = useQueryClient()
  const reread = useMutation({
    mutationFn: () => post<ProjectFileReread>(`/api/projects/${enc(p.key)}/project-file/apply`, {}),
    onSuccess: () => setTimeout(() => void qc.invalidateQueries({ queryKey: ['project-file', p.key] }), 3000),
  })
  const download = (text: string) => {
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/yaml' }))
    a.download = 'project.yml'
    a.click()
    URL.revokeObjectURL(a.href)
  }
  return (
    <Card title="Project file">
      <div className="space-y-2 p-3 text-sm">
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={exp.isPending} onClick={() => exp.mutate(undefined, { onSuccess: (r) => download(r.text) })}>Export project.yml</Button>
          <span className="text-xs text-fg-subtle">Everything this project is, with secret names but no values. Commit it as .trisof/project.yml.</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={reread.isPending} onClick={() => reread.mutate()}>Read the file again</Button>
          <span className="text-xs text-fg-subtle">{reread.data ? <>Applying <Mono>{reread.data.sha.slice(0, 7)}</Mono> from {reread.data.branch}…</> : "From the head of the project's branch."}</span>
        </div>
        {reread.error && <ErrorState error={reread.error} compact />}
        {exp.data && exp.data.problems.length > 0 && <div className="text-warn text-xs">Fix before committing: {exp.data.problems.join('; ')}</div>}
        {exp.error && <ErrorState error={exp.error} compact />}
        {q.error ? <ErrorState error={q.error} compact /> : q.isPending ? <SkeletonRows rows={2} /> : !s ? (
          <p className="text-xs text-fg-subtle">No .trisof/project.yml applied yet. Once one is merged to the project's branch, it decides these settings and the ones it names can no longer drift.</p>
        ) : (
          <>
            <div>In use: {short(s.goodSha)} · last read {short(s.sha)} <Age from={s.updatedAt} /></div>
            {s.status === 'invalid' && (
              <div className="text-err">
                The latest file was not applied; the last good one is still in use.
                <ul className="list-disc pl-5">{s.problems.map((x, i) => <li key={i} className="font-mono text-xs">{x}</li>)}</ul>
              </div>
            )}
            {s.drift && (s.drift.drift.length ? (
              <div className="text-warn">
                Changed by hand since the file was applied (checked <Age from={s.driftAt ?? 0} />):
                <ul className="list-disc pl-5">{s.drift.drift.map((d, i) => <li key={i}><span className="font-mono text-xs">{d.what}</span>: {d.detail}</li>)}</ul>
              </div>
            ) : <div className="text-ok">Everything matches the file (checked <Age from={s.driftAt ?? 0} />).</div>)}
            {s.drift && s.drift.unchecked.length > 0 && <div className="text-xs text-fg-subtle">Not checked (no account connected, or it did not answer): {s.drift.unchecked.join(', ')}</div>}
          </>
        )}
      </div>
    </Card>
  )
}

function CiCard({ p }: { p: Project }) {
  const m = useSave(p)
  const on = p.settings.ci?.enabled === true
  return (
    <Card title="CI">
      <div className="space-y-2 p-3">
        <div className="flex items-center justify-between gap-4">
          <label id="ci-on" htmlFor="ci-on-switch" className="text-sm text-fg">Run CI here (.trisof/ci.yml) instead of GitHub Actions</label>
          <Switch id="ci-on-switch" aria-labelledby="ci-on" checked={on} disabled={m.isPending} onChange={(v) => m.mutate({ settings: { ci: { ...p.settings.ci, enabled: v } } })} />
        </div>
        <p className="text-xs text-fg-subtle">On: every push and pull request runs the repo's .trisof/ci.yml on our CI servers and reports to GitHub as checks. Off: nothing here runs and the project keeps its GitHub Actions.</p>
        {m.error && <ErrorState error={m.error} compact />}
      </div>
    </Card>
  )
}

const errText = (e: unknown) => (e instanceof ApiError ? (e.what ?? e.message) : String(e))

